/**
 * Sidebar alert composition, lifted unchanged out of SidebarMenu.js so both
 * the old and new sidebars share one source of truth for "which alerts
 * show" while both shells coexist behind the app-shell flag. Rendering
 * itself is already shared too — both SidebarMenu.js and the new
 * Sidebar.jsx import the same SidebarNotifications/SidebarAlerts.
 */
import { useMemo } from "react";
import { useAppStore } from "../../../../hooks/redux/app";
import { useResendActivationAlert } from "./SidebarNotifications/useResendActivationAlert";
import { useUpgradePlanAlert } from "./SidebarNotifications/useUpgradePlanAlert";
import { useFailedPayment1Alert } from "./SidebarNotifications/useFailedPayment1Alert";
import { useFailedPayment2Alert } from "./SidebarNotifications/useFailedPayment2Alert";
import { useSyncIssuesAlerts } from "./SidebarNotifications/useSyncIssuesAlerts";
import { useBecomeAffiliateAlert } from "./SidebarNotifications/useBecomeAffiliateAlert";
import { useQuotaViewsAlert } from "./SidebarNotifications/useQuotaViewsAlert";
import { useDNSProblemAlert } from "./SidebarNotifications/useDNSProblemAlert";

export function useSidebarAlerts() {
  const { dismissAlert } = useAppStore();

  // Sorted critical → warning → info → promo and capped at two by
  // SidebarAlerts itself — order here only matters as the "newest first"
  // tiebreak within a variant (these are synthetic; there's no real
  // created_at to sort by).
  const resendActivationAlert = useResendActivationAlert();
  const failedPayment1Alert = useFailedPayment1Alert();
  const failedPayment2Alert = useFailedPayment2Alert();
  const syncIssuesAlerts = useSyncIssuesAlerts();
  const quotaViewsAlert = useQuotaViewsAlert();
  const dnsProblemAlert = useDNSProblemAlert();
  const becomeAffiliateAlert = useBecomeAffiliateAlert();
  const upgradePlanAlert = useUpgradePlanAlert();

  const alerts = useMemo(
    () =>
      [
        failedPayment1Alert,
        failedPayment2Alert,
        ...syncIssuesAlerts,
        quotaViewsAlert,
        resendActivationAlert,
        dnsProblemAlert,
        becomeAffiliateAlert,
        upgradePlanAlert,
      ].filter(Boolean),
    [
      failedPayment1Alert,
      failedPayment2Alert,
      syncIssuesAlerts,
      quotaViewsAlert,
      resendActivationAlert,
      dnsProblemAlert,
      becomeAffiliateAlert,
      upgradePlanAlert,
    ]
  );

  return { alerts, dismissAlert };
}
